"use client";

import { api } from "@/lib/api-client";
import type { DetailedOwnListing } from "@/lib/types";
import { type PropsWithChildren, useEffect, useState } from "react";
import {
  type CreateAsParams,
  newListingButtonContext,
  type SellerAgentParams,
} from "../../_hooks/use-new-listing-button";
import { useBroadcastChannel } from "@/hooks/use-broadcast-channel";

type NewListingEvent = {
  type: "creating" | "created" | "errored";
};

export function NewListingButtonProvider({ children }: PropsWithChildren) {
  const [isDisabled, setIsDisabled] = useState(false);

  const createListingMutation = api.listings.create.useMutation();

  const { postMessage, closeChannel } = useBroadcastChannel<NewListingEvent>(
    "venturu_new_listing",
    (data) => {
      if (data.type === "creating") {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    },
  );

  async function createListing(
    createAs: CreateAsParams | undefined,
    sellerAgent: SellerAgentParams | undefined,
    onListingCreated: (listing: DetailedOwnListing) => void | Promise<void>,
  ) {
    if (isDisabled) {
      return;
    }

    postMessage({ type: "creating" });
    setIsDisabled(true);

    try {
      const { listing } = await createListingMutation.mutateAsync({
        createAs,
        sellerAgent,
      });

      onListingCreated(listing);
    } catch (err) {
      console.error(err);
    } finally {
      postMessage({ type: "created" });
      setIsDisabled(false);
    }
  }

  useEffect(() => {
    return () => {
      closeChannel();
    };
  }, []);

  return (
    <newListingButtonContext.Provider value={{ isDisabled, createListing }}>
      {children}
    </newListingButtonContext.Provider>
  );
}
